import React, { type ComponentType } from 'react';
import { uid } from 'react-uid';
import type {
	ProjectStyle,
	ProjectType,
} from '@jira/platform__project-types/src/project-types.tsx';
import { useProject } from '@jira/navigation-apps__sidebar-common/src/controllers/project/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import type {
	Layout,
	Option,
	WithProjectTypeOptions,
	GlobalComponent,
	RightSidebar,
	ProjectContext,
} from './types.tsx';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { layoutWithCondition } from './utils/layout-with-condition';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type {
	// eslint-disable-next-line jira/export/restrict-types-re-export
	Layout,
	// eslint-disable-next-line jira/export/restrict-types-re-export
	Option,
	// eslint-disable-next-line jira/export/restrict-types-re-export
	WithProjectTypeOptions,
	// eslint-disable-next-line jira/export/restrict-types-re-export
	GlobalComponent,
	// eslint-disable-next-line jira/export/restrict-types-re-export
	RightSidebar,
	// eslint-disable-next-line jira/export/restrict-types-re-export
	ProjectContext,
};

/**
 * @deprecated Unneeded, and complicates code readability as it's not a standard language method and therefore is unknown to most of developers.
 * Use `str || str2` instead of `coalesce(str) ?? str2`
 */
const coalesce = (maybeString?: string | null): string | undefined =>
	maybeString == null || maybeString === '' ? undefined : maybeString;

export const renderGlobalComponents = (
	components: ReadonlyArray<ComponentType<{}>>,
): React.JSX.Element[] =>
	components.map((Component, i) => {
		const { displayName, name } = Component;
		const placeholderName = (coalesce(displayName) ?? coalesce(name) ?? String(i)).replaceAll(
			'/',
			'_',
		);
		const key = uid(Component);

		return (
			// TODO Only add suspense if lazy component, otherwise add suspense within component array
			<Placeholder name={String(placeholderName)} fallback={<></>} key={key}>
				<Component />
			</Placeholder>
		);
	});

/**
 * This factory creates a Layout and ensures that any of the provided components are unique instances, which is
 * important when using composition by concatenation.
 *
 * In practice, when rendering a component list from the layout, using this factory will:
 *
 * 1. Prevent rendering the same component multiple times
 * 2. Prevent runtime failures when using the component name as a key
 *
 * @param globalComponents The list of global components that will be rendered in the page container
 * @param rightSidebars The list of right sidebar components that will be rendered in the page container
 * @returns {Layout} The layout to be used in a route definition
 *
 * @see https://reactjs.org/docs/lists-and-keys.html
 */
export const createLayout = ({
	globalComponents = [],
	rightSidebars = [],
	preloadComponents,
	isChromeless = false,
}: Partial<Layout> = {}): Layout => {
	const layout = {
		isChromeless,
		globalComponents: Array.from(new Set(globalComponents)),
		rightSidebars: Array.from(new Set(rightSidebars)),
		preloadComponents: Array.from(new Set(preloadComponents)),
	};

	return layout;
};

export const preloadComponents = (layout: Layout): void => {
	layout.preloadComponents?.forEach((component) => {
		component.preload?.();
	});
};

/**
 * This function combines all of the slots in each of the layouts via composition.
 *
 * @param layouts The list of layouts to compose with one another
 * @returns {Layout} The layout to be used in a route definition
 */
export const composeLayouts = (...layouts: ReadonlyArray<Partial<Layout>>): Layout => {
	if (layouts.length === 1) {
		return createLayout(layouts[0]);
	}

	const layout = layouts.reduce<Layout>(
		(l, { globalComponents = [], rightSidebars = [], isChromeless = false }) => ({
			isChromeless,
			globalComponents: [...l.globalComponents, ...globalComponents],
			rightSidebars: [...l.rightSidebars, ...rightSidebars],
		}),
		{ globalComponents: [], rightSidebars: [], isChromeless: false },
	);

	return createLayout(layout);
};

const capitalize = (str: string) => `${str.charAt(0).toUpperCase()}${str.slice(1)}`;

const formatPrefix = (style?: Option<ProjectStyle>, type?: Option<ProjectType>) => {
	if (!style && !type) {
		return 'Empty';
	}

	if (style === 'any' && type === 'any') {
		return 'Any';
	}

	const formattedStyle = capitalize(style === 'next-gen' ? 'nextGen' : (style ?? ''));
	if (type === 'any') {
		return formattedStyle;
	}

	const formattedType = capitalize(type === 'service_desk' ? 'service' : (type ?? ''));
	if (style === 'any') {
		return formattedType;
	}

	return `${formattedStyle}${formattedType}`;
};

/**
 * This function creates a higher-order component, that renders the provided component when the useProject hook matches
 * the input options. Unfortunately, this is necessary as long as we have ambiguous routes, and ensures that components
 * do not unmount during transitions.
 *
 * TODO This HOC should be removed when route paths are no longer ambiguous
 *
 * @param Component The component to enhance, and render
 * @param options The conditions that need to be matched in order to render the provided component
 * @returns {function(): (null|*)} A component that will only be rendered when the project context matches the provided
 * options
 */
export const withProjectContext = (
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	Component: ComponentType<Record<any, any>>,
	options: WithProjectTypeOptions, // oxlint-disable-next-line @typescript-eslint/no-explicit-any
): ComponentType<Record<any, any>> => {
	const { project: expectedProject } = options;

	const ScopedComponent = () => {
		const { data: project, loading } = useProject();

		if (loading) {
			return null;
		}

		if (
			(expectedProject?.type === 'any' ? !project : project?.type !== expectedProject?.type) ||
			(expectedProject?.style === 'any' ? !project : project?.style !== expectedProject?.style)
		) {
			return null;
		}

		return <Component />;
	};

	const name = coalesce(Component.displayName) ?? coalesce(Component.name) ?? 'Component';
	const prefix = formatPrefix(expectedProject?.style, expectedProject?.type);

	ScopedComponent.displayName = `with${prefix}ProjectContext(${name})`;

	return ScopedComponent;
};
